import {
  commitTransaction,
  generateTaxBreakdownForInvoice,
  lockTransaction,
  TransactionListItem,
  TransactionStatus,
  voidTransaction,
} from '@commenda/api';
import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToggle,
} from '@commenda/kakashi';
import { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import TransactionActionModal from './modal/TransactionActionModal';

export const TransactionRowActionMenu = ({
  transaction,
  corporationId,
  refetchTransactions,
  salesTaxTestMode = false,
  isInternalUser = false,
}: {
  transaction: TransactionListItem;
  corporationId: number;
  salesTaxTestMode?: boolean;
  refetchTransactions: () => void;
  isInternalUser?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const transactionActionModalVisibility = useToggle();
  const [action, setAction] = useState<TransactionStatus | null>(null);

  const { setNotification } = useNotification();

  const markTransactionAsVoid = async () => {
    const response = await voidTransaction(transaction.id, corporationId);
    if (response.success) {
      refetchTransactions();
      transactionActionModalVisibility.hide();
      setNotification({
        show: true,
        valid: true,
        title: 'Transaction marked as void',
        message: 'Transaction marked as void',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to mark transaction as void',
        message: 'Failed to mark transaction as void',
      });
    }
  };

  const markTransactionAsCommitted = async () => {
    const response = await commitTransaction(transaction.id, corporationId);
    if (response.success) {
      refetchTransactions();
      transactionActionModalVisibility.hide();
      setNotification({
        show: true,
        valid: true,
        title: 'Transaction committed',
        message: 'Transaction committed',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to commit transaction',
        message: 'Failed to commit transaction',
      });
    }
  };

  const markTransactionAsLocked = async () => {
    const response = await lockTransaction(transaction.id, corporationId);
    if (response.success) {
      refetchTransactions();
      transactionActionModalVisibility.hide();
      setNotification({
        show: true,
        valid: true,
        title: 'Transaction locked',
        message: 'Transaction locked',
      });
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to lock transaction',
        message: 'Failed to lock transaction',
      });
    }
  };

  const onActionModalClose = () => {
    setAction(null);
    transactionActionModalVisibility.hide();
  };

  const handleTaxBreakdownForInvoice = async (invoiceId: string) => {
    try {
      const response = await generateTaxBreakdownForInvoice(
        corporationId,
        invoiceId
      );

      if (response.success) {
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Tax breakdown generation in progress',
        });
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            response.error ||
            'Failed to start tax breakdown generation process',
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to start tax breakdown generation process',
      });
    }
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverContent className="w-[220px] p-0" align="end">
          <div className="py-2">
            {transaction.status === TransactionStatus.UNCOMMITTED && (
              <>
                {isInternalUser && (
                  <div
                    className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAction(TransactionStatus.COMMITTED);
                      setIsOpen(false);
                      transactionActionModalVisibility.show();
                    }}
                  >
                    <span className="txt-small-medium text-text-secondary">
                      Mark as to be filed
                    </span>
                  </div>
                )}
                <div
                  className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAction(TransactionStatus.VOIDED);
                    setIsOpen(false);
                    transactionActionModalVisibility.show();
                  }}
                >
                  <span className="txt-small-medium text-text-error">
                    Mark as void
                  </span>
                </div>
              </>
            )}
            {transaction.status === TransactionStatus.COMMITTED && (
              <>
                {isInternalUser && (
                  <div
                    className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAction(TransactionStatus.LOCKED);
                      setIsOpen(false);
                      transactionActionModalVisibility.show();
                    }}
                  >
                    <span className="txt-small-medium text-text-secondary">
                      Mark as locked
                    </span>
                  </div>
                )}

                <div
                  className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAction(TransactionStatus.VOIDED);
                    setIsOpen(false);
                    transactionActionModalVisibility.show();
                  }}
                >
                  <span className="txt-small-medium text-text-error">
                    Mark as void
                  </span>
                </div>
              </>
            )}
            {(transaction.status === TransactionStatus.LOCKED ||
              transaction.status === TransactionStatus.VOIDED) && (
              <div className="flex items-center gap-2 h-10 px-3">
                <span className="txt-small-medium text-text-tertiary">
                  No actions available
                </span>
              </div>
            )}
            {salesTaxTestMode && (
              <div
                className="flex items-center gap-2 h-10 px-3 cursor-pointer hover:bg-bg-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTaxBreakdownForInvoice(transaction.id);
                }}
              >
                <span className="txt-small-medium text-text-secondary">
                  Generate tax breakdowns
                </span>
              </div>
            )}
          </div>
        </PopoverContent>
        <PopoverTrigger onClick={(e) => e.stopPropagation()}>
          <Button variant="tertiary" className="size-8 p-0" type="button">
            <Icons.DotsVertical className="size-4 shrink-0" />
          </Button>
        </PopoverTrigger>
      </Popover>

      <TransactionActionModal
        isOpen={action && transactionActionModalVisibility.visible}
        onClose={onActionModalClose}
        onVoid={markTransactionAsVoid}
        onCommit={markTransactionAsCommitted}
        onLock={markTransactionAsLocked}
        transaction={transaction}
        action={action}
      />
    </>
  );
};
